/**
 * Validator module exports
 */

// Schema validator
export {
  validateFlowDefinition,
  isValidFlowDefinition,
  formatValidationErrors,
  type ValidationError,
  type ValidationResult,
} from "./schema-validator.js";

// Expression validator
export {
  extractExpressions,
  extractExpressionsFromValue,
  hasExpression,
  validateExpression,
  validateExpressions,
  expressionReferencesAction,
  getReferencedActions,
  expressionReferencesTrigger,
  validateActionReferences,
  type ExpressionValidationResult,
} from "./expression-validator.js";

// Connection validator
export {
  extractRequiredConnections,
  validateConnections,
  extractConnectorIds,
  usesPremiumConnectors,
  getPremiumConnectors,
  suggestConnectionKey,
  buildConnectionReference,
  type ConnectionValidationResult,
} from "./connection-validator.js";

// Best practice validator
export {
  checkBestPractices,
  formatBestPracticeResults,
  type BestPracticeIssue,
  type BestPracticeResult,
} from "./best-practices.js";

// ============================================================================
// Combined Validation
// ============================================================================

import type { FlowDefinition, ConnectionReference } from "../api/types.js";
import { validateFlowDefinition, type ValidationResult } from "./schema-validator.js";
import { validateExpressions } from "./expression-validator.js";
import { validateConnections } from "./connection-validator.js";

/**
 * Full flow validation result
 */
export interface FullValidationResult {
  valid: boolean;
  schemaValidation: ValidationResult;
  expressionValidation: {
    valid: boolean;
    errors: Array<{ path: string; message: string; severity: "error" | "warning" }>;
    expressionCount: number;
  };
  connectionValidation: {
    valid: boolean;
    errors: Array<{ path: string; message: string; severity: "error" | "warning" }>;
    missingConnections: string[];
    unusedConnections: string[];
  };
}

/**
 * Perform full validation of a flow
 */
export function validateFlow(
  definition: FlowDefinition,
  connectionReferences: Record<string, ConnectionReference> = {}
): FullValidationResult {
  // Schema validation
  const schemaResult = validateFlowDefinition(definition);

  // Expression validation
  const exprResult = validateExpressions(definition, "definition");

  // Connection validation
  const connResult = validateConnections(definition, connectionReferences);

  return {
    valid: schemaResult.valid && exprResult.valid && connResult.valid,
    schemaValidation: schemaResult,
    expressionValidation: {
      valid: exprResult.valid,
      errors: exprResult.errors,
      expressionCount: exprResult.expressions.length,
    },
    connectionValidation: {
      valid: connResult.valid,
      errors: connResult.errors,
      missingConnections: Array.from(connResult.missingConnections),
      unusedConnections: Array.from(connResult.unusedConnections),
    },
  };
}

/**
 * Quick validation - returns true/false only
 */
export function isValidFlow(
  definition: FlowDefinition,
  connectionReferences: Record<string, ConnectionReference> = {}
): boolean {
  return validateFlow(definition, connectionReferences).valid;
}

/**
 * Get all validation errors as formatted strings
 */
export function getAllValidationErrors(result: FullValidationResult): string[] {
  const errors: string[] = [];

  // Schema errors
  for (const err of result.schemaValidation.errors) {
    errors.push(`[Schema] ${err.path}: ${err.message}`);
  }

  // Expression errors
  for (const err of result.expressionValidation.errors) {
    if (err.severity === "error") {
      errors.push(`[Expression] ${err.path}: ${err.message}`);
    }
  }

  // Connection errors
  for (const err of result.connectionValidation.errors) {
    if (err.severity === "error") {
      errors.push(`[Connection] ${err.path}: ${err.message}`);
    }
  }

  return errors;
}

/**
 * Get all validation warnings as formatted strings
 */
export function getAllValidationWarnings(result: FullValidationResult): string[] {
  const warnings: string[] = [];

  // Schema warnings
  for (const warn of result.schemaValidation.warnings) {
    warnings.push(`[Schema] ${warn.path}: ${warn.message}`);
  }

  // Expression warnings
  for (const warn of result.expressionValidation.errors) {
    if (warn.severity === "warning") {
      warnings.push(`[Expression] ${warn.path}: ${warn.message}`);
    }
  }

  // Connection warnings
  for (const warn of result.connectionValidation.errors) {
    if (warn.severity === "warning") {
      warnings.push(`[Connection] ${warn.path}: ${warn.message}`);
    }
  }

  return warnings;
}
